// Eskom PARCC — OpenText link receipt (R5 #4).
//
// A link counts as RECEIVED when the imported "Link Provided" flag is set OR a
// link was captured on the transaction (`Transaction.link` — the OpenText URL
// or reference, imported via the Link/Links/OpenText Link columns or entered on
// the CES page). The flag alone under-counted: links pasted as OpenText text
// were reported as outstanding even though they were in fact provided.
// Content is not validated here — a faulty link (see `linkIssue`) was still
// received; faultiness is tracked independently.
//
// ---------------------------------------------------------------------------
// Three-state link tracking (Lindiwe R6, shipped Aug 2026 — owner feedback #8).
//
// A link is derivable from the two fields we store, with no schema change:
//     link text present (non-placeholder)      -> Received
//     linkProvided === true, but no link text  -> Pending
//     neither                                  -> Outstanding
// `linkState()` is the authority; `linkReceived()` is exactly
// (linkState === 'Received'). Measured against the 8 Aug population (497 rows):
// Received 419 / Pending 36 / Outstanding 42 — the 36 Pending rows all carry
// `link provided = Yes` with an EMPTY OpenText link cell (previously counted as
// Received). This is a correction, briefed to the client before it lands.
//
// FAULTY is independent of state: `linkFaulty()` reads the "Issue with links"
// column. That column is answered "No" on 283 rows (i.e. NO issue) — the old
// "non-blank ⇒ faulty" rule therefore reported 283 faulty links when the true
// count is 0. `linkFaulty()` treats negations/blank as not-faulty.
// ---------------------------------------------------------------------------

/** The link fields this predicate reads (structural subset of Transaction). */
export interface LinkFields {
  linkProvided?: boolean;
  link?: string;
}

/** The three link-tracking states a transaction can be in. */
export type LinkState = 'Received' | 'Pending' | 'Outstanding';

/**
 * Import cells that mean "no link" rather than being one — consultants fill
 * unavailable links with these (same family as the text-backfill placeholder
 * set). Anything else non-blank counts: OpenText references are not always
 * URLs ("Content Server node 998877").
 */
const LINK_PLACEHOLDERS = new Set([
  'no',
  'none',
  'n/a',
  'na',
  '-',
  'nil',
  'tbc',
  'pending',
  'awaiting',
  'not provided',
  'not available',
  'outstanding',
]);

/**
 * True when a real (non-placeholder) link reference is captured — i.e. there is
 * something clickable on file. Exported so the OpenText anchors gate on this
 * instead of raw truthiness (a placeholder cell like "TBC" must not render a dead
 * `<a href="TBC">` that dead-ends against the app origin).
 */
export function hasRealLink(t: LinkFields): boolean {
  const link = String(t.link ?? '').trim();
  return link !== '' && !LINK_PLACEHOLDERS.has(link.toLowerCase());
}

/**
 * The link-tracking state of a transaction:
 *   • Received    — a real link reference is on file.
 *   • Pending     — marked provided (linkProvided) but no link captured yet.
 *   • Outstanding — neither.
 */
export function linkState(t: LinkFields): LinkState {
  if (hasRealLink(t)) return 'Received';
  if (t.linkProvided === true) return 'Pending';
  return 'Outstanding';
}

/** True when a transaction's supporting-document link was received (has a real link). */
export function linkReceived(t: LinkFields): boolean {
  return linkState(t) === 'Received';
}

// "Issue with links" answers that mean there is NO issue (so NOT faulty). The
// column is a Yes/No / free-text answer: only a positive/descriptive answer is a
// fault. Blank and these negations are clean (owner feedback Aug 2026: "No" was
// being miscounted as faulty on 283 transactions).
const NO_ISSUE_ANSWERS = new Set(['no', 'none', 'n/a', 'na', '-', 'nil', 'n', 'false', 'ok', 'okay']);

/** The fields the faulty-link predicate reads. */
export interface LinkIssueFields {
  linkIssue?: string;
}

/**
 * True when a link fault was logged. Independent of {@link linkState} — a faulty
 * link may still have been received. A blank answer, or an explicit negation
 * ("No", "None", "N/A", …), is NOT a fault; any other answer is.
 */
export function linkFaulty(t: LinkIssueFields): boolean {
  const issue = String(t.linkIssue ?? '').trim().toLowerCase();
  if (issue === '') return false;
  return !NO_ISSUE_ANSWERS.has(issue);
}
